import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock, Award, Play, CheckCircle, Filter } from 'lucide-react';
import { useModules } from '../contexts/ModuleContext';
import { useAuth } from '../contexts/AuthContext';
import { graphqlService } from '../services/graphqlService';

const TrainingCalendar = () => {
  const { modules, getModulesForUser } = useModules();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [userProgress, setUserProgress] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month, week, day, agenda
  const [filterMode, setFilterMode] = useState('all'); // all, upcoming, thisWeek, thisMonth
  const [showFilters, setShowFilters] = useState(false);
  const [allTrainingEvents, setAllTrainingEvents] = useState([]);

  // Handle event click - navigate to training module
  const handleEventClick = (event, e) => {
    e.stopPropagation(); // Prevent calendar day click
    if (event.moduleId) {
      navigate(`/module/${event.moduleId}`);
    }
  };

  useEffect(() => {
    if (!user || !user.email) return;

    const loadProgress = async () => {
      try {
        const progress = await graphqlService.getProgress(user.email);
        setUserProgress(progress || {});
      } catch (error) {
        console.error('Error loading progress:', error);
      }
    };

    loadProgress();
  }, [user]);

  const [assignedModules, setAssignedModules] = useState([]);

  useEffect(() => {
    if (!user) {
      setAssignedModules([]);
      return;
    }

    const loadModules = async () => {
      try {
        const userModules = await getModulesForUser(user);
        setAssignedModules(Array.isArray(userModules) ? userModules : []);
      } catch (error) {
        console.error('Error loading assigned modules:', error);
        setAssignedModules([]);
      }
    };

    loadModules();
  }, [user, getModulesForUser]);

  // Get event type icon
  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'training': return <Play size={16} />;
      case 'review': return <CheckCircle size={16} />;
      case 'quiz': return <Award size={16} />;
      case 'scheduled': return <Calendar size={16} />;
      default: return <Calendar size={16} />;
    }
  };

  // Map moduleId to event type code for coloring
  const getEventTypeFromModuleId = (moduleId) => {
    if (!moduleId) return 'scheduled';
    
    if (moduleId.includes('credentials-verification-service') || moduleId.includes('cvs')) {
      return 'cvs';
    }
    if (moduleId.includes('credentials-evaluation-service') || moduleId.includes('ces')) {
      return 'ces';
    }
    if (moduleId.includes('visascreen') || moduleId.includes('vs')) {
      return 'vs';
    }
    if (moduleId.includes('specialty-certification') || moduleId.includes('sc')) {
      return 'sc';
    }
    if (moduleId.includes('documentation') || moduleId.includes('doc')) {
      return 'doc';
    }
    if (moduleId.includes('certification-program')) {
      return 'sc'; // Certification programs use specialty certification color
    }
    
    return 'scheduled'; // Default fallback
  };


  // Check if user is assigned to a schedule
  const isUserAssignedToSchedule = (schedule, user) => {
    if (!user || !schedule.assignments) {
      return false;
    }

    const assignments = schedule.assignments;
    
    // Check role assignment (case-insensitive comparison)
    if (assignments.roles && assignments.roles.length > 0) {
      const userRoleLower = (user.role || '').toLowerCase();
      const hasRoleMatch = assignments.roles.some(role => 
        (role || '').toLowerCase() === userRoleLower
      );
      if (hasRoleMatch) {
        return true;
      }
    }

    // Check department assignment (case-insensitive comparison)
    if (assignments.departments && assignments.departments.length > 0 && user.department) {
      const userDeptLower = user.department.toLowerCase();
      const hasDeptMatch = assignments.departments.some(dept => 
        (dept || '').toLowerCase() === userDeptLower
      );
      if (hasDeptMatch) {
        return true;
      }
    }

    // Check designation assignment (case-insensitive comparison)
    if (assignments.designations && assignments.designations.length > 0 && user.designation) {
      const userDesignationLower = user.designation.toLowerCase();
      const hasDesignationMatch = assignments.designations.some(designation => 
        (designation || '').toLowerCase() === userDesignationLower
      );
      if (hasDesignationMatch) {
        return true;
      }
    }

    // Check specialization assignment (case-insensitive comparison)
    if (assignments.specializations && assignments.specializations.length > 0 && user.specialization) {
      const userSpecializationLower = user.specialization.toLowerCase();
      const hasSpecializationMatch = assignments.specializations.some(specialization => 
        (specialization || '').toLowerCase() === userSpecializationLower
      );
      if (hasSpecializationMatch) {
        return true;
      }
    }

    // Check individual user assignment (case-insensitive email comparison)
    if (assignments.users && assignments.users.length > 0 && user.email) {
      const userEmailLower = user.email.toLowerCase();
      const hasUserMatch = assignments.users.some(email => 
        (email || '').toLowerCase() === userEmailLower
      );
      if (hasUserMatch) {
        return true;
      }
    }

    return false;
  };

  // Load scheduled events when user or assignedModules change
  useEffect(() => {
    if (!user || !user.email) {
      setAllTrainingEvents([]);
      return;
    }

    const loadEvents = async () => {
      try {
        const schedules = await graphqlService.getAllSchedules();
        if (!schedules || schedules.length === 0) {
          setAllTrainingEvents([]);
          return;
        }

        const events = [];

        schedules.forEach((schedule) => {
          // Check if current user is assigned to this schedule
          const isAssigned = isUserAssignedToSchedule(schedule, user);
          if (!isAssigned) return;

          // Find the module for this schedule
          const module = assignedModules.find(m => m.id === schedule.moduleId);

          // Create events for each day of the scheduled training
          const startDate = new Date(schedule.startDate);
          const endDate = new Date(schedule.endDate);
          const currentDate = new Date(startDate);

          while (currentDate <= endDate) {
            const eventType = getEventTypeFromModuleId(schedule.moduleId);
            events.push({
              id: `${schedule.id}-${currentDate.toISOString().split('T')[0]}`,
              title: schedule.title,
              type: eventType,
              moduleId: schedule.moduleId,
              scheduleId: schedule.id,
              date: new Date(currentDate),
              startTime: schedule.startTime,
              endTime: schedule.endTime,
              duration: module?.duration || 60,
              difficulty: module?.difficulty || 'Intermediate',
              description: schedule.description,
              location: schedule.location,
              instructor: schedule.instructor,
              icon: getEventTypeIcon(eventType)
            });

            currentDate.setDate(currentDate.getDate() + 1);
          }
        });

        const sortedEvents = events.sort((a, b) => a.date - b.date);
        setAllTrainingEvents(sortedEvents);
      } catch (error) {
        console.error('Error loading scheduled events:', error);
        setAllTrainingEvents([]);
      }
    };

    loadEvents();
  }, [user, assignedModules]);

  // Filter events based on selected filter mode
  const getFilteredEvents = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    switch (filterMode) {
      case 'upcoming':
        return allTrainingEvents.filter(event => event.date >= today);
      case 'thisWeek':
        return allTrainingEvents.filter(event => 
          event.date >= startOfWeek && event.date <= endOfWeek
        );
      case 'thisMonth':
        return allTrainingEvents.filter(event => 
          event.date >= startOfMonth && event.date <= endOfMonth
        );
      default:
        return allTrainingEvents;
    }
  };

  const trainingEvents = getFilteredEvents();

  // Calendar navigation
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const navigateWeek = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (direction * 7));
      return newDate;
    });
  };

  const navigateDay = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + direction);
      return newDate;
    });
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return trainingEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Get events for current view
  const getEventsForCurrentView = () => {
    if (viewMode === 'month') {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      return trainingEvents.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= startOfMonth && eventDate <= endOfMonth;
      });
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return trainingEvents.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= startOfWeek && eventDate <= endOfWeek;
      });
    } else {
      return getEventsForDate(currentDate);
    }
  };

  // Render month view
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDay = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const dayEvents = getEventsForDate(currentDay);
      const isCurrentMonth = currentDay.getMonth() === month;
      const isToday = currentDay.toDateString() === new Date().toDateString();
      const isSelected = selectedDate && currentDay.toDateString() === selectedDate.toDateString();

      days.push(
        <div
          key={i}
          className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
          onClick={() => setSelectedDate(new Date(currentDay))}
        >
          <div className="day-number">{currentDay.getDate()}</div>
          <div className="day-events">
            {dayEvents.map((event, index) => (
              <div
                key={event.id}
                className={`event-block event-${event.type}`}
                style={{ 
                  backgroundColor: getEventTypeColor(event.type).bg,
                  color: getEventTypeColor(event.type).text,
                  cursor: event.moduleId ? 'pointer' : 'default'
                }}
                title={`${event.title} - ${event.instructor || 'No instructor'} - ${event.location || 'No location'}`}
                onClick={(e) => handleEventClick(event, e)}
              >
                <div className="event-block-title">{event.title.replace(' - Training', '').replace(' - Review', '').replace(' - Quiz', '')}</div>
                <div className="event-block-instructor">{event.instructor || 'TBA'}</div>
                <div className="event-block-location">{event.location || 'TBA'}</div>
              </div>
            ))}
          </div>
        </div>
      );

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return (
      <div className="calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="calendar-header">{day}</div>
        ))}
        {days}
      </div>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      const dayEvents = getEventsForDate(day);
      const isToday = day.toDateString() === new Date().toDateString();

      days.push(
        <div key={i} className={`week-day ${isToday ? 'today' : ''}`}>
          <div className="week-day-header">
            <div className="week-day-name">
              {day.toLocaleDateString('en-US', { weekday: 'short' })}
            </div>
            <div className="week-day-number">{day.getDate()}</div>
          </div>
          <div className="week-day-events">
            {dayEvents.map(event => (
              <div
                key={event.id}
                className={`week-event event-${event.type}`}
                style={{ 
                  backgroundColor: getEventTypeColor(event.type).bg,
                  color: getEventTypeColor(event.type).text,
                  cursor: event.moduleId ? 'pointer' : 'default'
                }}
                onClick={(e) => {
                  if (event.moduleId) {
                    e.stopPropagation();
                    handleEventClick(event, e);
                  } else {
                    setSelectedDate(day);
                  }
                }}
              >
                <div className="event-time">
                  {event.date.toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  })}
                </div>
                <div className="event-title">{event.title.replace(' - Training', '').replace(' - Review', '').replace(' - Quiz', '')}</div>
                <div className="event-type-badge">{event.type}</div>
                <div className="event-duration">{event.duration} min</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return <div className="week-grid">{days}</div>;
  };

  // Render day view
  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);
    const isToday = currentDate.toDateString() === new Date().toDateString();

    return (
      <div className="day-view">
        <div className="day-header">
          <div className="day-date">
            {currentDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
            {isToday && <span className="today-badge">Today</span>}
          </div>
        </div>
        <div className="day-events-list">
          {dayEvents.length > 0 ? (
            dayEvents.map(event => (
              <div 
                key={event.id} 
                className={`day-event event-${event.type}`}
                style={{ 
                  borderLeftColor: getEventTypeColor(event.type).text,
                  cursor: event.moduleId ? 'pointer' : 'default'
                }}
                onClick={(e) => event.moduleId && handleEventClick(event, e)}
              >
                <div className="event-icon">{event.icon}</div>
                <div className="event-content">
                  <div className="event-title">{event.title.replace(' - Training', '').replace(' - Review', '').replace(' - Quiz', '')}</div>
                  <div className="event-type-large">{event.type}</div>
                  <div className="event-description">{event.description}</div>
                  <div className="event-meta">
                    <span className="event-time">
                      <Clock size={14} />
                      {event.startTime ? `${event.startTime} - ${event.endTime}` : event.date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </span>
                    <span className="event-duration">
                      {event.duration} min
                    </span>
                    <span className={`event-difficulty difficulty-${event.difficulty.toLowerCase()}`}>
                      {event.difficulty}
                    </span>
                    {event.location && (
                      <span className="event-location">
                        📍 {event.location}
                      </span>
                    )}
                    {event.instructor && (
                      <span className="event-instructor">
                        👨‍🏫 {event.instructor}
                      </span>
                    )}
                  </div>
                </div>
                <div className="event-actions">
                  <button className="btn btn-sm btn-primary">
                    <Play size={14} />
                    Start
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-events">
              <Calendar size={48} className="no-events-icon" />
              <p>No training events scheduled for this day</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render agenda view
  const renderAgendaView = () => {
    const sortedEvents = [...trainingEvents].sort((a, b) => a.date - b.date);
    
    return (
      <div className="agenda-view">
        <div className="agenda-header">
          <h3 className="text-xl font-bold mb-4">Training Agenda</h3>
          <p className="text-gray-600 mb-6">
            {filterMode === 'all' ? 'All scheduled training events' : 
             filterMode === 'upcoming' ? 'Upcoming training events' :
             filterMode === 'thisWeek' ? 'This week\'s training events' :
             'This month\'s training events'}
          </p>
        </div>
        
        <div className="agenda-events">
          {sortedEvents.length > 0 ? (
            sortedEvents.map(event => {
              const eventDate = new Date(event.date);
              const isToday = eventDate.toDateString() === new Date().toDateString();
              const isPast = eventDate < new Date();
              
              return (
                <div 
                  key={event.id} 
                  className={`agenda-event event-${event.type} ${isPast ? 'past-event' : ''}`}
                  style={{ 
                    borderLeftColor: getEventTypeColor(event.type),
                    cursor: event.moduleId ? 'pointer' : 'default'
                  }}
                  onClick={(e) => event.moduleId && handleEventClick(event, e)}
                >
                  <div className="agenda-event-date">
                    <div className="agenda-date-number">{eventDate.getDate()}</div>
                    <div className="agenda-date-month">{eventDate.toLocaleDateString('en-US', { month: 'short' })}</div>
                    <div className="agenda-date-year">{eventDate.getFullYear()}</div>
                  </div>
                  
                  <div className="agenda-event-content">
                    <div className="agenda-event-header">
                      <h4 className="agenda-event-title">{event.title}</h4>
                      <div className="agenda-event-time">
                        {event.startTime ? `${event.startTime} - ${event.endTime}` : 
                         eventDate.toLocaleTimeString('en-US', { 
                           hour: 'numeric', 
                           minute: '2-digit',
                           hour12: true 
                         })}
                      </div>
                    </div>
                    
                    <div className="agenda-event-details">
                      <div className="agenda-event-meta">
                        <span className="agenda-meta-item">
                          <Clock size={14} />
                          {event.duration} min
                        </span>
                        <span className={`agenda-difficulty difficulty-${event.difficulty.toLowerCase()}`}>
                          {event.difficulty}
                        </span>
                        {event.location && (
                          <span className="agenda-meta-item">
                            📍 {event.location}
                          </span>
                        )}
                        {event.instructor && (
                          <span className="agenda-meta-item">
                            👨‍🏫 {event.instructor}
                          </span>
                        )}
                      </div>
                      
                      <p className="agenda-event-description">{event.description}</p>
                    </div>
                  </div>
                  
                  <div className="agenda-event-actions">
                    <button className={`btn btn-sm ${isPast ? 'btn-secondary' : 'btn-primary'}`}>
                      <Play size={14} />
                      {isPast ? 'Review' : 'Start'}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-events">
              <Calendar size={48} className="no-events-icon" />
              <p>No training events found for the selected filter</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Get event type color - using different light shades
  const getEventTypeColor = (type) => {
    // Light color pairs with different shades: [backgroundColor, textColor]
    const colorPairs = [
      { bg: '#e0f2fe', text: '#0284c7' }, // Light Sky Blue
      { bg: '#f0fdf4', text: '#16a34a' }, // Light Green
      { bg: '#fef3c7', text: '#d97706' }, // Light Amber
      { bg: '#fce7f3', text: '#db2777' }, // Light Pink
      { bg: '#e9d5ff', text: '#9333ea' }, // Light Purple
      { bg: '#d1fae5', text: '#059669' }, // Light Emerald
      { bg: '#fed7aa', text: '#ea580c' }, // Light Orange
      { bg: '#cffafe', text: '#0891b2' }  // Light Cyan
    ];
    
    // Map event types to colors using hash-based selection (same logic as UserManagement)
    const hash = (type || 'scheduled').split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return colorPairs[Math.abs(hash) % colorPairs.length];
  };


  return (
    <div className="container">
      <div className="training-calendar">
      <div className="calendar-header">
        <div className="calendar-title">
          <Calendar size={24} />
          <h2>Training Calendar</h2>
        </div>
        
        <div className="calendar-controls">
          <div className="view-toggle">
            <button 
              className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
            <button 
              className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => setViewMode('week')}
            >
              Week
            </button>
            <button 
              className={`view-btn ${viewMode === 'day' ? 'active' : ''}`}
              onClick={() => setViewMode('day')}
            >
              Day
            </button>
            <button 
              className={`view-btn ${viewMode === 'agenda' ? 'active' : ''}`}
              onClick={() => setViewMode('agenda')}
            >
              Agenda
            </button>
          </div>
          
          <div className="calendar-navigation">
            <button 
              className="nav-btn"
              onClick={() => {
                if (viewMode === 'month') navigateMonth(-1);
                else if (viewMode === 'week') navigateWeek(-1);
                else navigateDay(-1);
              }}
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="current-date">
              {viewMode === 'month' && currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              {viewMode === 'week' && `Week of ${currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
              {viewMode === 'day' && currentDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            
            <button 
              className="nav-btn"
              onClick={() => {
                if (viewMode === 'month') navigateMonth(1);
                else if (viewMode === 'week') navigateWeek(1);
                else navigateDay(1);
              }}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <button 
            className="today-btn"
            onClick={() => setCurrentDate(new Date())}
          >
            Today
          </button>

          <button 
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filters
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      {showFilters && (
        <div className="filter-panel">
          <div className="filter-group">
            <label className="filter-label">Time Range:</label>
            <div className="filter-options">
              <button 
                className={`filter-option ${filterMode === 'all' ? 'active' : ''}`}
                onClick={() => setFilterMode('all')}
              >
                All Events
              </button>
              <button 
                className={`filter-option ${filterMode === 'upcoming' ? 'active' : ''}`}
                onClick={() => setFilterMode('upcoming')}
              >
                Upcoming
              </button>
              <button 
                className={`filter-option ${filterMode === 'thisWeek' ? 'active' : ''}`}
                onClick={() => setFilterMode('thisWeek')}
              >
                This Week
              </button>
              <button 
                className={`filter-option ${filterMode === 'thisMonth' ? 'active' : ''}`}
                onClick={() => setFilterMode('thisMonth')}
              >
                This Month
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="calendar-content">
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'day' && renderDayView()}
        {viewMode === 'agenda' && renderAgendaView()}
      </div>

      {/* Event Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: getEventTypeColor('ces').bg }}></div>
          <span>CES Training</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: getEventTypeColor('cvs').bg }}></div>
          <span>CVS</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: getEventTypeColor('vs').bg }}></div>
          <span>VisaScreen®</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: getEventTypeColor('sc').bg }}></div>
          <span>Specialty Certification</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: getEventTypeColor('doc').bg }}></div>
          <span>Documentation</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: getEventTypeColor('ctp').bg }}></div>
          <span>CTP</span>
        </div>
      </div>
    </div>
    </div>
  );
};

export default TrainingCalendar;
